import { useState, type ReactNode } from 'react';
import type { LucideIcon } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Input,
  Label,
} from '@clarix/ui';
import { cn } from '@clarix/ui/lib/cn';

type Tone = 'default' | 'destructive' | 'primary';

const TONE_BADGE: Record<'default' | 'destructive', string> = {
  destructive: 'bg-destructive/10 text-destructive',
  default: 'bg-primary/10 text-primary',
};

export interface ConfirmDialogProps {
  open: boolean;
  /** Close handler. Pass this or `onClose` — both fire on Cancel, Esc and backdrop. */
  onOpenChange?: (open: boolean) => void;
  /** Convenience close handler for parents that only care about the close event. */
  onClose?: () => void;
  title: ReactNode;
  /**
   * The consequence, in one sentence. Reads at full foreground weight — it's the
   * line the user decides on. Pass a node to emphasise the record being acted on
   * (e.g. `<>This will delete <strong>{name}</strong>.</>`).
   */
  description?: ReactNode;
  /** Muted supporting line under the description — side-effects, where to undo, caveats. */
  footnote?: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  /** Confirm label while the action runs. Defaults to `'Working…'`. */
  loadingLabel?: string;
  /** Primary button colour. `'primary'` is accepted as an alias of `'default'`. */
  variant?: Tone;
  /** Alias of `variant`, for call sites that speak in tones. */
  tone?: Tone;
  /** When true, buttons disable and the confirm label flips to `loadingLabel`. */
  loading?: boolean;
  /** Alias of `loading`, for call sites passing a React Query `isPending`. */
  isPending?: boolean;
  /** Extra guard on the confirm button, beyond `loading`. */
  confirmDisabled?: boolean;
  /** If set, the user must type this exact string before confirm enables. */
  requireTyped?: string;
  /** Leading icon badge. Omit for the quiet default (no icon). */
  icon?: LucideIcon;
  /** Allow backdrop-click / Esc to close. Default true. */
  dismissable?: boolean;
  onConfirm: () => void | Promise<void>;
}

/**
 * The single confirmation modal for the whole ecosystem. Replaces ad-hoc
 * `window.confirm()` calls and per-app copies so destructive actions look and
 * behave the same everywhere.
 *
 * Parent controls `open` and clears it after the async action resolves; the
 * dialog stays open while `loading` is true so users see feedback.
 *
 * Deliberately quiet by default: no icon, no dividers, a `shadow-sm` lift. An
 * alert this small is already the interruption. Pass `icon` when a surface wants
 * the badge treatment, and `requireTyped` for truly irreversible actions.
 */
export function ConfirmDialog({
  open,
  onOpenChange,
  onClose,
  title,
  description,
  footnote,
  confirmLabel,
  cancelLabel = 'Cancel',
  loadingLabel = 'Working…',
  variant,
  tone,
  loading = false,
  isPending = false,
  confirmDisabled = false,
  requireTyped,
  icon: Icon,
  dismissable = true,
  onConfirm,
}: ConfirmDialogProps) {
  const [typed, setTyped] = useState('');
  const busy = loading || isPending;
  const resolvedTone = variant ?? tone ?? 'default';
  const isDestructive = resolvedTone === 'destructive';
  const canConfirm = !requireTyped || typed === requireTyped;

  const requestChange = (next: boolean) => {
    // Never let a click-away or Esc close the dialog mid-request.
    if (busy && !next) return;
    if (!next && !dismissable) return;
    if (!next) setTyped('');
    onOpenChange?.(next);
    if (!next) onClose?.();
  };

  return (
    <AlertDialog open={open} onOpenChange={requestChange}>
      <AlertDialogContent className="gap-5 border-0 p-5 shadow-sm">
        <AlertDialogHeader className="space-y-2 text-left">
          <div className={cn(Icon && 'flex items-start gap-3')}>
            {Icon && (
              <span
                className={cn(
                  'flex h-8 w-8 shrink-0 items-center justify-center rounded-full',
                  TONE_BADGE[isDestructive ? 'destructive' : 'default'],
                )}
              >
                <Icon className="h-4 w-4" strokeWidth={1.5} aria-hidden />
              </span>
            )}
            <div className="space-y-2">
              <AlertDialogTitle>{title}</AlertDialogTitle>
              {(description || footnote) && (
                <div className="space-y-1">
                  {description && (
                    <AlertDialogDescription className="text-foreground">
                      {description}
                    </AlertDialogDescription>
                  )}
                  {footnote && <p className="text-muted-foreground text-lg">{footnote}</p>}
                </div>
              )}
            </div>
          </div>
        </AlertDialogHeader>

        {requireTyped && (
          <div className="space-y-1.5">
            <Label htmlFor="confirm-typed" className="text-muted-foreground text-lg">
              Type <code className="text-foreground font-mono">{requireTyped}</code> to confirm
            </Label>
            <Input
              id="confirm-typed"
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              autoComplete="off"
              autoFocus
            />
          </div>
        )}

        {/* `sm:space-x-0` + `gap` so the button spacing is set in ONE place
            (the base footer spaces its children with `sm:space-x-2`). */}
        <AlertDialogFooter className="gap-1.5 sm:space-x-0">
          <AlertDialogCancel className="border-muted-foreground/40 h-7" disabled={busy}>
            {cancelLabel}
          </AlertDialogCancel>
          <AlertDialogAction
            disabled={busy || confirmDisabled || !canConfirm}
            onClick={(e) => {
              e.preventDefault();
              void (async () => {
                await onConfirm();
                setTyped('');
              })();
            }}
            className={cn(
              'h-7',
              isDestructive &&
                'bg-destructive text-destructive-foreground hover:bg-destructive/90 focus-visible:ring-destructive',
            )}
          >
            {busy ? loadingLabel : (confirmLabel ?? (isDestructive ? 'Delete' : 'Confirm'))}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
